import React, { useState, useEffect } from "react";
import { NavLink } from 'react-router-dom';
import { getCDNImage } from '../globals';
import { T } from "react-translator-component";
import LangSelector from './LangSelector';
import { faConciergeBell, faHome, faClipboardCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import '../scss/_public_person_panel.scss';
import { isHotelCGBeach } from "./CheckIn/hotelConditions";

/**
 * PublicPersonPanel Component
 * 
 * Componente reutilizable que renderiza una sección hero con:
 * - Imagen de fondo personalizable
 * - Selector de idioma
 * - Información del usuario (avatar/logo, nombre, email)
 * - Barra de navegación con botones
 * 
 * @param {Object} props - Las propiedades del componente
 * @param {Object} props.hotel - Datos del hotel (settings, logo)
 * @param {Object} props.person - Datos de la persona (fullname, email)
 * @param {function} props.getUrlPath - Función para generar rutas con URL del hotel
 * @param {Array} props.navItems - (Opcional) Items de navegación personalizados
 *                                  Default: [{ label: "home", icon: faHome }, { label: "services", icon: faConciergeBell }]
 * @param {string} props.backgroundImage - (Opcional) URL de imagen de fondo
 * @param {string} props.backgroundColor - (Opcional) Color de fondo (fallback si no hay imagen)
 * @param {boolean} props.showLanguageSelector - (Opcional) Mostrar selector de idioma (default: true)
 * @param {boolean} props.showUserInfo - (Opcional) Mostrar información del usuario (default: true)
 * @param {boolean} props.showNavBar - (Opcional) Mostrar barra de navegación (default: true)
 * 
 * @example
 * <PublicPersonPanel 
 *   hotel={hotel} 
 *   person={person}
 *   getUrlPath={getUrlPath}
 * />
 * 
 * @example
 * <PublicPersonPanel 
 *   hotel={hotel} 
 *   person={person}
 *   getUrlPath={getUrlPath}
 *   navItems={[
 *     { label: "home", icon: faHome, path: "/" },
 *     { label: "services", icon: faConciergeBell, path: "/public/services" },
 *     { label: "custom", icon: faUser, path: "/custom" }
 *   ]}
 *   showLanguageSelector={true}
 *   showUserInfo={true}
 * />
 */
const PublicPersonPanel = ({
  hotel = {},
  person = {},
  getUrlPath = (path) => path,
  navItems = null,
  backgroundImage = null,
  backgroundColor = "linear-gradient(0deg, rgba(152,132,96,1) 35%, rgba(153,153,153,1) 90%)",
  showLanguageSelector = true,
  showUserInfo = true,
  showNavBar = true,
}) => {
  // Items de navegación por defecto
  const defaultNavItems = [
    {
      label: "home",
      icon: faClipboardCheck,
      path: "/public/",
      translationKey: "public.dashboard.requests"
    },
    {
      label: "services",
      icon: faConciergeBell,
      path: "/public/services",
      translationKey: "public.dashboard.services"
    }
  ];

  const [navigationItems, setNavigationItems] = useState([]);

  // Combinar ambos useEffect en uno solo
  useEffect(() => {
    // Si es hotel CG Beach, usar items específicos
    if(isHotelCGBeach(hotel?.id)) {
      setNavigationItems([
        {
          label: "home",
          icon: faClipboardCheck,
          path: "/public/",
          translationKey: "public.dashboard.requests"
        },
        {
          label: "services",
          icon: faConciergeBell,
          path: "/public_cart",
          translationKey: "public.dashboard.cgbeach.public_cart"
        }
      ]);
    } else {
      setNavigationItems(navItems || defaultNavItems);
    }
  }, [navItems, hotel?.id]);

  // Determinar el fondo
  const heroBackground = backgroundImage || 
    (hotel?.settings?.homeSliderImages?.[0]
      ? `linear-gradient(to bottom, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 1) 95%), url(${getCDNImage(hotel.settings.homeSliderImages[0])})`
      : backgroundColor);

  // Si no hay person, retornar vacío
  if (!person?.fullname) {
    return null;
  }

  return (
    <div className="dashboard-hero-section">
      
      {/* Hero Background with Slider Image */}
      <div 
        className="dashboard-hero-background"
        style={{
          background: heroBackground,
        }}
      />

      {/* Language Selector - Positioned over background */}
      {showLanguageSelector && (
        <div className="dashboard-language-selector-fixed">
          <LangSelector />
        </div>
      )}

      {/* User Profile Section - Centered in hero */}
      {showUserInfo && (
        <div className="dashboard-user-info-hero">
          <div className="user-avatar-large">
            {hotel?.logo ? (
              <img
                src={getCDNImage(hotel.logo)}
                className="hotel-logo-status"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                  backgroundPosition: "center",
                }}
                alt="Hotel Logo"
              />
            ) : (
              <span className="avatar-initial-large">
                {person?.fullname?.charAt(0)?.toUpperCase() || 'G'}
              </span>
            )}
          </div>
          <h2 className="hero-user-name">{person?.fullname ?? "--"}</h2>
          <h2 className="hero-user-email">{person?.email}</h2>
        </div>
      )}

      {/* Navigation Button Bar */}
      {showNavBar && navigationItems && navigationItems.length > 0 && (
        <div className="dashboard-top-button-bar">
          {navigationItems.map((item, index) => (
            <React.Fragment key={`nav-item-${index}`}>
              <NavLink
                to={getUrlPath(item.path)}
                className={({ isActive }) => `btn-dashboard-${item.label} ${isActive ? 'active' : ''}`}
              >
                <FontAwesomeIcon 
                  icon={item.icon} 
                  size="md" 
                  style={{ marginRight: 6, marginTop: -2 }} 
                />
                <span>{T(item.translationKey)}</span>
              </NavLink>
              
              {/* Divider - no mostrar en el último item */}
              {index < navigationItems.length - 1 && (
                <div className="border-left" style={{ height: 30 }}></div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default PublicPersonPanel;

